import { useState } from 'react';
import { trackEvent } from '@/lib/analytics';

export type LeadFormStatus = 'idle' | 'success' | 'error';

/**
 * Shared submit lifecycle for the site's lead-capture forms (Contact,
 * Discovery Trial, ...). All of them POST to the same /api/submit-form
 * contract and only differ in payload shape, so the fetch/status-tracking
 * plumbing lives here once instead of being copy-pasted per page.
 */
export function useLeadFormSubmit() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<LeadFormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const submit = async (payload: Record<string, unknown>): Promise<boolean> => {
    setIsSubmitting(true);
    setStatus('idle');
    setErrorMessage('');

    try {
      const response = await fetch('/api/submit-form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (result.success) {
        setStatus('success');
        const label = (payload.subject as string) || (payload.formType as string) || 'unknown';
        trackEvent('lead_form', 'submit', label);
        return true;
      }
      setStatus('error');
      setErrorMessage(result.error || 'Something went wrong');
      return false;
    } catch (_error) {
      setStatus('error');
      setErrorMessage('Network error. Please try again.');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const reset = () => {
    setStatus('idle');
    setErrorMessage('');
  };

  return { isSubmitting, status, errorMessage, submit, reset };
}
